// Mock data for the Enterprise AI Value & Absorption Sandbox
// NOTE: This is MOCKED front-end data used to make the dashboard feel complete.

export const READINESS_LAYERS = [
  { key: 'technology', order: 1, en: 'Technology & Infrastructure', ar: 'التقنية والبنية التحتية', color: '#6366F1' },
  { key: 'data', order: 2, en: 'Data Readiness', ar: 'جاهزية البيانات', color: '#8B5CF6' },
  { key: 'process', order: 3, en: 'Process & Workflow', ar: 'العمليات وسير العمل', color: '#EC4899' },
  { key: 'behaviour', order: 4, en: 'Behaviour & Ownership', ar: 'السلوك والملكية', color: '#F59E0B' },
  { key: 'governance', order: 5, en: 'Governance & Value Capture', ar: 'الحوكمة والتقاط القيمة', color: '#10B981' },
];

export const FUNCTIONS = [
  { key: 'customer', en: 'Customer Service', ar: 'خدمة العملاء' },
  { key: 'operations', en: 'Operations', ar: 'العمليات' },
  { key: 'finance', en: 'Finance', ar: 'المالية' },
  { key: 'hr', en: 'Human Resources', ar: 'الموارد البشرية' },
  { key: 'marketing', en: 'Marketing', ar: 'التسويق' },
  { key: 'supply', en: 'Supply Chain', ar: 'سلسلة التوريد' },
  { key: 'it', en: 'Information Technology', ar: 'تقنية المعلومات' },
  { key: 'sales', en: 'Sales', ar: 'المبيعات' },
  { key: 'legal', en: 'Legal & Compliance', ar: 'القانون والامتثال' },
  { key: 'rnd', en: 'Research & Development', ar: 'البحث والتطوير' },
];

export const STATUSES = [
  { key: 'backlog', en: 'Backlog', ar: 'قائمة الانتظار', color: '#94A3B8' },
  { key: 'piloting', en: 'Piloting', ar: 'قيد التجربة', color: '#F59E0B' },
  { key: 'live', en: 'Live', ar: 'مباشر', color: '#6366F1' },
  { key: 'scaled', en: 'Scaled', ar: 'موسّع', color: '#10B981' },
];

const TITLES = {
  customer: ['AI Contact Centre Copilot', 'Automated Ticket Triage', 'Sentiment & Escalation Detection', 'Self-Service Knowledge Assistant', 'Voice Call Summaries', 'Multilingual Support Bot', 'Complaint Root-Cause Analysis', 'Proactive Churn Outreach', 'Agent Response Suggestions', 'Live Chat Deflection'],
  operations: ['Predictive Maintenance', 'Quality Defect Detection', 'Shift Scheduling Optimiser', 'Downtime Root-Cause AI', 'Energy Consumption Optimiser', 'Field Ops Assistant', 'Asset Utilisation Forecast', 'Incident Classification', 'SOP Generation Assistant', 'Process Mining Insights'],
  finance: ['Invoice Data Extraction', 'Fraud Anomaly Detection', 'Cash-Flow Forecasting', 'Automated Reconciliation', 'Expense Policy Auditor', 'Financial Report Drafting', 'Credit Risk Scoring', 'Spend Analytics Copilot', 'Audit Evidence Retrieval', 'Budget Variance Explainer'],
  hr: ['CV Screening Assistant', 'Interview Note Summaries', 'Employee Query Bot', 'Attrition Risk Prediction', 'Onboarding Copilot', 'Skills Gap Analyser', 'Policy Q&A Assistant', 'Performance Review Drafting', 'Learning Path Recommender', 'Sentiment Pulse Analysis'],
  marketing: ['Campaign Copy Generator', 'Audience Segmentation AI', 'SEO Content Assistant', 'Creative Variant Testing', 'Social Listening Insights', 'Personalised Email Engine', 'Brand Compliance Checker', 'Market Trend Summaries', 'Lead Scoring Model', 'Ad Spend Optimiser'],
  supply: ['Demand Forecasting', 'Inventory Optimisation', 'Supplier Risk Monitoring', 'Logistics Route Planner', 'Shipment Delay Prediction', 'Procurement Copilot', 'Warehouse Slotting AI', 'Contract Clause Extraction', 'Spend Category Insights', 'Delivery ETA Prediction'],
  it: ['IT Helpdesk Copilot', 'Code Review Assistant', 'Log Anomaly Detection', 'Automated Runbook Generation', 'Security Alert Triage', 'Documentation Assistant', 'Test Case Generation', 'Cloud Cost Optimiser', 'Incident Postmortem Drafts', 'Access Request Assistant'],
  sales: ['Deal Intelligence Copilot', 'Meeting Note Summaries', 'Proposal Generator', 'Next-Best-Action Engine', 'Lead Enrichment AI', 'Pipeline Health Insights', 'Competitor Battlecards', 'Email Draft Assistant', 'Forecast Accuracy Model', 'Territory Optimiser'],
  legal: ['Contract Review Assistant', 'Regulatory Change Monitor', 'Clause Risk Detection', 'Legal Research Copilot', 'NDA Drafting Assistant', 'Compliance Gap Analysis', 'Policy Summarisation', 'e-Discovery Retrieval', 'Obligation Tracking', 'Redaction Automation'],
  rnd: ['Research Literature Synthesis', 'Experiment Design Assistant', 'Patent Landscape Analysis', 'Material Discovery AI', 'Simulation Summariser', 'Hypothesis Generator', 'Data Labelling Copilot', 'Formulation Optimiser', 'Technical Report Drafting', 'Knowledge Graph Builder'],
};

function seededRand(seed) {
  let x = Math.sin(seed) * 10000;
  return x - Math.floor(x);
}

function buildUseCases() {
  const cases = [];
  let id = 1;
  FUNCTIONS.forEach((fn, fi) => {
    TITLES[fn.key].forEach((title, ti) => {
      const seed = fi * 13 + ti * 7 + 1;
      const value = Math.round(40 + seededRand(seed) * 60);
      const absorption = Math.round(20 + seededRand(seed + 1) * 75);
      const layers = {};
      READINESS_LAYERS.forEach((l, li) => {
        layers[l.key] = Math.round(30 + seededRand(seed + li + 2) * 70);
      });
      const avgReady = Math.round(Object.values(layers).reduce((a, b) => a + b, 0) / 5);
      let status = 'backlog';
      if (avgReady > 78 && absorption > 65) status = 'scaled';
      else if (avgReady > 62) status = 'live';
      else if (avgReady > 48) status = 'piloting';
      cases.push({
        id: `UC-${String(id).padStart(3, '0')}`,
        title,
        function: fn.key,
        description: `Applies generative and predictive AI to ${title.toLowerCase()} within ${fn.en.toLowerCase()}, targeting measurable value with sustained workflow adoption.`,
        value,
        absorption,
        annualValue: Math.round((value * 12 + seededRand(seed + 9) * 400)) * 1000,
        layers,
        readiness: avgReady,
        status,
        owner: ['A. Al-Thani', 'S. Kumar', 'M. Hassan', 'L. Rossi', 'F. Ahmed', 'J. Park'][id % 6],
      });
      id++;
    });
  });
  return cases;
}

export const USE_CASES = buildUseCases();

export const OVERVIEW_STATS = {
  totalUseCases: USE_CASES.length,
  scaled: USE_CASES.filter((u) => u.status === 'scaled').length,
  live: USE_CASES.filter((u) => u.status === 'live').length,
  piloting: USE_CASES.filter((u) => u.status === 'piloting').length,
  backlog: USE_CASES.filter((u) => u.status === 'backlog').length,
  avgAbsorption: Math.round(USE_CASES.reduce((a, u) => a + u.absorption, 0) / USE_CASES.length),
  avgValue: Math.round(USE_CASES.reduce((a, u) => a + u.value, 0) / USE_CASES.length),
  totalAnnualValue: USE_CASES.reduce((a, u) => a + u.annualValue, 0),
};

export const LAYER_AVERAGES = READINESS_LAYERS.map((l) => ({
  ...l,
  score: Math.round(USE_CASES.reduce((a, u) => a + u.layers[l.key], 0) / USE_CASES.length),
}));
